/**
 * Their page in the business's own colours: every token the page draws with comes from the brand's
 * accent, secondary and paper, the way the confirmed prototype derives them (its applyLook()).
 * The page lives in daylight, so a dark paper gives way to a faint tint of the accent.
 */
import type { CSSProperties } from "react";
import type { PublicBrand } from "../../../shared/types";

type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
const FALLBACK = { accent: "#7a2338", brass: "#b0873a", paper: "#fbf6f1" };

function hexToRgb(hex: string): Rgb | null {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const n = Number.parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const rgbHex = (r: Rgb) => `#${r.map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, "0")).join("")}`;
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const rgbs = (r: Rgb) => r.map((x) => Math.round(x)).join(",");

export function luminance(r: Rgb): number {
  const [x, y, z] = r.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * x + 0.7152 * y + 0.0722 * z;
}
export function contrast(a: Rgb, b: Rgb): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
/** Move a colour toward a goal in small steps until it passes the test. */
function toward(c: Rgb, goal: Rgb, ok: (c: Rgb) => boolean): Rgb {
  let k = 0;
  let out = c;
  while (!ok(out) && k < 1) {
    k += 0.03;
    out = mix(c, goal, k);
  }
  return out;
}

/** The page's tokens for this brand, as CSS custom properties on the page's root. */
export function lookTokens(brand: Pick<PublicBrand, "accent" | "brass" | "paper">): Record<string, string> {
  const a = hexToRgb(brand.accent) ?? (hexToRgb(FALLBACK.accent) as Rgb);
  const b = hexToRgb(brand.brass) ?? (hexToRgb(FALLBACK.brass) as Rgb);
  const paperIn = hexToRgb(brand.paper);
  // Why: the page is home in daylight; a dark paper (a dark-mode brand) would put dark ink on a dark ground.
  const t = paperIn && luminance(paperIn) >= 0.35 ? paperIn : mix(WHITE, a, 0.035);
  const i = mix(a, BLACK, 0.74);
  const d = mix(a, BLACK, 0.87);
  // Why: the tint is the ground and the conversation is a brighter sheet of paper on it.
  const paper = mix(t, WHITE, 0.58);
  const card = mix(t, WHITE, 0.86);
  const mayaBub = mix(mix(t, i, 0.07), a, 0.012);
  const me = toward(a, BLACK, (c) => contrast(c, WHITE) >= 4.6);
  const atext = toward(a, BLACK, (c) => contrast(c, t) >= 4.8);
  const ink2 = toward(mix(i, t, 0.3), i, (c) => contrast(c, mayaBub) >= 7);
  const ink3 = toward(mix(i, t, 0.5), i, (c) => contrast(c, mayaBub) >= 4.6);
  const brass = toward(b, BLACK, (c) => contrast(c, paper) >= 4.6);
  const deep = mix(mix(d, a, 0.16), BLACK, 0.12);
  const deep2 = mix(d, a, 0.3);
  // Why: a dark accent disappears on the dark capsule, so its glow lifts the accent while white text stays readable.
  let lit = toward(me, WHITE, (c) => contrast(c, deep) >= 2.4);
  if (contrast(lit, WHITE) < 4.5) lit = toward(lit, BLACK, (c) => contrast(c, WHITE) >= 4.5);
  const brassLit = toward(b, WHITE, (c) => contrast(c, deep2) >= 6);
  return {
    "--ground": rgbHex(t),
    "--paper": rgbHex(paper),
    "--card": rgbHex(card),
    "--maya-bub": rgbHex(mayaBub),
    "--ink": rgbHex(i),
    "--ink-rgb": rgbs(i),
    "--ink-2": rgbHex(ink2),
    "--ink-3": rgbHex(ink3),
    "--line": `rgba(${rgbs(i)},.09)`,
    "--line-2": `rgba(${rgbs(i)},.15)`,
    "--me": rgbHex(me),
    "--accent-text": rgbHex(atext),
    "--accent-wash": `rgba(${rgbs(a)},.08)`,
    "--accent-wash-2": `rgba(${rgbs(a)},.14)`,
    "--accent-line": `rgba(${rgbs(a)},.32)`,
    "--brass": rgbHex(brass),
    "--deep": rgbHex(deep),
    "--deep-2": rgbHex(deep2),
    "--lit": rgbHex(lit),
    "--brass-lit": rgbHex(brassLit),
    "--shadow": rgbs(d),
  };
}

/** The same tokens as an inline style for the page's root. */
export const lookStyle = (brand: Pick<PublicBrand, "accent" | "brass" | "paper">): CSSProperties =>
  lookTokens(brand) as CSSProperties;

export { hexToRgb };
